// @ts-nocheck
/* Portado verbatim de games_content/seminario-tese/ena-ona-seminario.jsx
   (componente de apresentação self-contained; KaTeX/Fonts via CDN).
   Renderizado como slide type=custom (componentId: ena-ona-seminario). */
import React, { useState, useEffect, useContext, createContext } from "react";

/* ════════════════════════════════════════════════════════════════════
   ENA & ONA — como funciona a codificação e a análise
   --------------------------------------------------------------------
   Animação interativa (modo Cenas, estilo Mestre PV) para o Seminário
   de Tese. Apresenta a Análise de Rede Epistêmica (ENA) e a Análise de
   Rede Ordenada (ONA) a partir de um caso concreto: um aluno explorando
   um evento do ATLAS no HYPATIA durante uma Masterclass do IPPOG.

   A aula avança em cenas; cada cena combina:
     • uma NARRATIVA breve que apresenta a etapa
     • uma CONFIGURAÇÃO do canvas (qual visualização aparece)
     • uma PERGUNTA de previsão que o aluno responde antes
     • um INSIGHT que aparece após o aluno se comprometer

   Cenas:
     1) O mistério — dois alunos sabem o mesmo, pensam igual?
     2) Codificar — transformar fala em presença de elementos
     3) Stanza — co-ocorrência na mesma estrofe = conexão
     4) Matriz de adjacência — o esqueleto numérico da rede
     5) A rede (ENA) — a abstração; conexões simétricas
     6) ENA × ONA — a ordem importa numa narrativa
     7) Fechamento — aplicar à tese (novato → frame-alvo do CERN)

   Prof. Paulo Vicente — IFBA Simões Filho · PPGDC
   Uso didático. Padrão visual Física Interativa.
   ════════════════════════════════════════════════════════════════════ */

// ── Dimensões do canvas ──────────────────────────────────────────────
const WIDTH = 680;
const HEIGHT = 470;

// ── Paleta (padrão Física Interativa) ────────────────────────────────
const C = {
  blue: "#2d4a8a",    blueSoft: "#5b7ac4",    blueBg: "#2d4a8a14",
  red: "#a83232",     redSoft: "#d06b6b",     redBg: "#a8323214",
  green: "#3d7a2f",   greenSoft: "#6fa85f",   greenBg: "#3d7a2f14",
  violet: "#5b3f8f",  violetSoft: "#8c70bf",  violetBg: "#5b3f8f14",
  orange: "#b05510",  orangeSoft: "#d88550",  orangeBg: "#b0551014",
  amber: "#b87510",   amberSoft: "#e09438",   amberBg: "#b8751014",
  gray: "#64748b",    gline: "#cbd5e1",       grayBg: "#64748b14",
  bg: "#f1f5f9",
  card: "#ffffff",
  border: "#cbd5e1",
  borderStrong: "#94a3b8",
  text: "#1e293b",
  muted: "#475569",
  dim: "#64748b",
  dimmer: "#94a3b8",
};

// ── Os cinco códigos do frame epistêmico do físico de partículas ─────
// Pedagogicamente: cada código é um elemento do "pensar como físico".
// Cor codifica o tipo de elemento e é estável em toda a animação —
// o aluno aprende a associação em segundos.
const CODES = [
  { id: "K.det",  label: "lê o detector",          color: C.blue   },
  { id: "K.part", label: "identifica partícula",   color: C.violet },
  { id: "S.med",  label: "mede massa invariante",  color: C.orange },
  { id: "E.evi",  label: "justifica c/ evidência", color: C.green  },
  { id: "E.inc",  label: "reconhece incerteza",    color: C.amber  },
];
const IDX = Object.fromEntries(CODES.map((c, i) => [c.id, i]));
const getCode = (id) => CODES[IDX[id]];

// ── A transcrição do aluno explorando o evento no HYPATIA ────────────
// É a stanza "Evento 4": cada linha é uma fala, codificada para os
// elementos que aparecem nela.
const LINES = [
  { t: "Tem dois rastros bem retos saindo do ponto de colisão",        codes: ["K.det"] },
  { t: "Esse rastro chega na câmara de múons — então é um múon",       codes: ["K.det", "K.part", "E.evi"] },
  { t: "Vou marcar os dois e pedir a massa invariante",                codes: ["S.med"] },
  { t: "Deu 91 GeV — bate com a massa do bóson Z",                     codes: ["S.med", "E.evi"] },
  { t: "Com um evento só não dá pra afirmar, preciso ver mais",        codes: ["E.inc"] },
];

// ── Conexões (não-direcionadas) que emergem da co-ocorrência ─────────
// Pares que apareceram juntos numa mesma fala da stanza.
const COOC = [
  ["K.det", "K.part"],
  ["K.det", "E.evi"],
  ["K.part", "E.evi"],
  ["S.med", "E.evi"],
];

// ── Conexões ORDENADAS (ONA): a seta segue a ordem do raciocínio ─────
// Tudo desemboca em "justificar com evidência" — E.evi é um sorvedouro.
const COOC_DIR = [
  ["K.det", "K.part"],
  ["K.part", "E.evi"],
  ["K.det", "E.evi"],
  ["S.med", "E.evi"],
];

// ── Posições dos nós na rede (cenas 5 e 6) ───────────────────────────
const NET = {
  "K.det":  [185, 160],
  "K.part": [340, 110],
  "E.evi":  [360, 255],
  "S.med":  [515, 185],
  "E.inc":  [195, 345],
};

// ── Posições dos nós no espaço de projeção (cena 7) ──────────────────
const PROJ = {
  "K.det":  [255, 120],
  "K.part": [385, 140],
  "S.med":  [520, 215],
  "E.evi":  [360, 290],
  "E.inc":  [255, 375],
};
// Novato: liga conhecimento/dados/evidência, deixa a incerteza solta.
const PROJ_NOVATO = [
  ["K.det", "K.part"], ["K.part", "S.med"], ["S.med", "E.evi"], ["K.det", "E.evi"],
];
// Frame-alvo: integra a incerteza ao restante do raciocínio.
const PROJ_ALVO = [
  ["K.det", "K.part"], ["K.part", "S.med"], ["S.med", "E.evi"], ["K.det", "E.evi"],
  ["E.evi", "E.inc"], ["K.part", "E.inc"],
];

// ── KaTeX via CDN (idempotente, com fallback Unicode) ────────────────
const KATEX_VERSION = "0.16.9";
const KATEX_CSS = `https://cdnjs.cloudflare.com/ajax/libs/KaTeX/${KATEX_VERSION}/katex.min.css`;
const KATEX_JS  = `https://cdnjs.cloudflare.com/ajax/libs/KaTeX/${KATEX_VERSION}/katex.min.js`;
const KatexCtx = createContext(false);

function useKatexLoader() {
  const [ready, setReady] = useState(typeof window !== "undefined" && !!window.katex);
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.katex) { setReady(true); return; }
    if (!document.querySelector("link[data-fi-katex]")) {
      const link = document.createElement("link");
      link.rel = "stylesheet"; link.href = KATEX_CSS;
      link.setAttribute("data-fi-katex", "true");
      document.head.appendChild(link);
    }
    let script = document.querySelector("script[data-fi-katex]");
    const onLoad = () => setReady(true);
    if (!script) {
      script = document.createElement("script");
      script.src = KATEX_JS; script.async = true;
      script.setAttribute("data-fi-katex", "true");
      document.head.appendChild(script);
    }
    script.addEventListener("load", onLoad);
    const id = setInterval(() => { if (window.katex) { setReady(true); clearInterval(id); } }, 80);
    return () => { clearInterval(id); if (script) script.removeEventListener("load", onLoad); };
  }, []);
  return ready;
}

function K({ tex, display = false, style, color }) {
  const ready = useContext(KatexCtx);
  const finalStyle = { ...(color && { color }), ...style };
  if (ready && typeof window !== "undefined" && window.katex) {
    const html = window.katex.renderToString(tex, {
      throwOnError: false, displayMode: display, output: "html",
    });
    return <span className="fi-katex" style={finalStyle}
      dangerouslySetInnerHTML={{ __html: html }} />;
  }
  const fallback = tex
    .replace(/\\vec\{([^}]+)\}/g, "$1\u20D7")
    .replace(/\\cdot/g, "·").replace(/\\times/g, "×")
    .replace(/_\{([^}]+)\}/g, "$1")
    .replace(/\\text\{([^}]+)\}/g, "$1")
    .replace(/\\mathrm\{([^}]+)\}/g, "$1");
  return <span style={{ ...finalStyle, fontStyle: "italic", fontFamily: "serif" }}>{fallback}</span>;
}

// ── Cabeça de seta poligonal côncava (padrão Física Interativa) ──────
function arrowHeadPoints(x, y, angle, size = 14, ha = 0.4, inset = 0.34) {
  const back = angle + Math.PI;
  return [
    [x, y],
    [x + size * Math.cos(back + ha), y + size * Math.sin(back + ha)],
    [x + size * inset * Math.cos(back), y + size * inset * Math.sin(back)],
    [x + size * Math.cos(back - ha), y + size * Math.sin(back - ha)],
  ].map((p) => p.join(",")).join(" ");
}

// Aresta direcionada: linha + cabeça poligonal, recuada para não sobrar
// linecap dentro da ponta.
function DirEdge({ from, to, color, width = 3, arrSize = 14, opacity = 1 }) {
  const dx = to[0] - from[0], dy = to[1] - from[1];
  const len = Math.hypot(dx, dy);
  if (len < 1) return null;
  const ang = Math.atan2(dy, dx);
  const ux = dx / len, uy = dy / len;
  // termina antes do nó de destino (raio do nó ~ 11) e antes da ponta
  const tx = to[0] - 13 * ux, ty = to[1] - 13 * uy;
  const ex = tx - arrSize * 0.32 * ux, ey = ty - arrSize * 0.32 * uy;
  const sx = from[0] + 13 * ux, sy = from[1] + 13 * uy;
  return (
    <g opacity={opacity}>
      <line x1={sx} y1={sy} x2={ex} y2={ey} stroke={color} strokeWidth={width} strokeLinecap="round" />
      <polygon points={arrowHeadPoints(tx, ty, ang, arrSize)} fill={color} />
    </g>
  );
}

// Nó da rede: bolinha colorida + rótulo do código
function Node({ id, pos, dim = false, r = 11 }) {
  const c = getCode(id);
  return (
    <g opacity={dim ? 0.45 : 1}>
      <circle cx={pos[0]} cy={pos[1]} r={r} fill={c.color} />
      <circle cx={pos[0]} cy={pos[1]} r={r} fill="none" stroke="#fff" strokeWidth={1.5} opacity={0.5} />
      <text x={pos[0]} y={pos[1] - r - 6} textAnchor="middle"
        fontFamily="'JetBrains Mono', monospace" fontSize={12} fontWeight={600} fill={c.color}>
        {id}
      </text>
    </g>
  );
}

// Etiqueta da etapa no canto superior esquerdo do canvas
function StageTag({ n, label }) {
  const w = 96 + label.length * 6.6;
  return (
    <g>
      <rect x={14} y={14} width={w} height={24} rx={6} fill={C.violetBg} stroke={C.violet} strokeWidth={1} />
      <text x={26} y={30} fontFamily="'JetBrains Mono', monospace" fontSize={11} fontWeight={700}
        fill={C.violet} letterSpacing="0.5">{`ETAPA ${n} · ${label}`}</text>
    </g>
  );
}

// ════════════════════════════════════════════════════════════════════
//  VISUALIZAÇÕES POR CENA
// ════════════════════════════════════════════════════════════════════

// Cena 1 — dois alunos, mesmos nós, estruturas diferentes
function TeaserView({ revealed }) {
  const pent = (cx, cy, r) =>
    CODES.map((c, i) => {
      const a = -Math.PI / 2 + (i * 2 * Math.PI) / 5;
      return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
    });
  const A = pent(180, 250, 78);
  const B = pent(500, 250, 78);
  // A integra a incerteza (índice 4); B deixa solta.
  const edgesA = [[0, 1], [1, 3], [3, 4], [0, 3], [2, 3]];
  const edgesB = [[0, 1], [1, 3], [0, 3], [2, 3]];
  return (
    <g>
      <StageTag n={"?"} label="MESMO CONTEÚDO" />
      {[{ pts: A, label: "Aluno A", edges: edgesA, col: C.green },
        { pts: B, label: "Aluno B", edges: edgesB, col: C.orange }].map((g, gi) => (
        <g key={gi}>
          <text x={g.pts[0][0] - (gi === 0 ? 0 : 0)} y={70} textAnchor="middle"
            fontFamily="'Atkinson Hyperlegible', sans-serif" fontSize={15} fontWeight={700}
            fill={C.text} style={{ display: "none" }}>{g.label}</text>
          <text x={gi === 0 ? 180 : 500} y={400} textAnchor="middle"
            fontFamily="'Atkinson Hyperlegible', sans-serif" fontSize={14} fontWeight={700} fill={C.muted}>
            {g.label}
          </text>
          {revealed && g.edges.map(([a, b], ei) => (
            <line key={ei} x1={g.pts[a][0]} y1={g.pts[a][1]} x2={g.pts[b][0]} y2={g.pts[b][1]}
              stroke={g.col} strokeWidth={3} strokeLinecap="round" opacity={0.75} />
          ))}
          {g.pts.map((p, i) => (
            <circle key={i} cx={p[0]} cy={p[1]} r={9} fill={getCode(CODES[i].id).color} />
          ))}
        </g>
      ))}
      {!revealed && (
        <text x={340} y={250} textAnchor="middle" fontFamily="'JetBrains Mono', monospace"
          fontSize={40} fontWeight={700} fill={C.dimmer}>?</text>
      )}
      {revealed && (
        <text x={340} y={250} textAnchor="middle" fontFamily="'Atkinson Hyperlegible', sans-serif"
          fontSize={13} fontStyle="italic" fill={C.muted}>
          <tspan x={340} dy={0}>mesmas peças,</tspan>
          <tspan x={340} dy={18}>estruturas diferentes</tspan>
        </text>
      )}
    </g>
  );
}

// Cena 2 — codificar a transcrição do HYPATIA
function CodingView({ revealed }) {
  const rowH = 80;
  const top = 50;
  return (
    <g>
      <StageTag n={2} label="CODIFICAR" />
      {LINES.map((ln, i) => {
        const y = top + i * rowH;
        const isPred = i === 1; // a fala da pergunta de previsão
        const hide = isPred && !revealed;
        return (
          <g key={i}>
            <rect x={16} y={y} width={648} height={rowH - 12} rx={10}
              fill={isPred ? C.amberBg : C.card}
              stroke={isPred ? C.amber : C.border} strokeWidth={isPred ? 1.5 : 1} />
            <foreignObject x={30} y={y + 8} width={378} height={rowH - 24}>
              <div xmlns="http://www.w3.org/1999/xhtml" style={{
                fontFamily: "'Atkinson Hyperlegible', sans-serif", fontSize: 13.5,
                lineHeight: 1.35, color: C.text, display: "flex", alignItems: "center",
                height: "100%",
              }}>
                <span style={{
                  fontFamily: "'JetBrains Mono', monospace", color: C.dim,
                  fontSize: 11, marginRight: 8,
                }}>{i + 1}</span>
                <span>"{ln.t}"</span>
              </div>
            </foreignObject>
            {ln.codes.map((cid, k) => {
              const cx = 424 + k * 64;
              const c = getCode(cid);
              if (hide) {
                return (
                  <g key={k}>
                    <rect x={cx} y={y + rowH / 2 - 22} width={56} height={22} rx={5}
                      fill="none" stroke={C.dimmer} strokeWidth={1} strokeDasharray="3 3" />
                    <text x={cx + 28} y={y + rowH / 2 - 6} textAnchor="middle"
                      fontFamily="'JetBrains Mono', monospace" fontSize={13} fill={C.dimmer}>?</text>
                  </g>
                );
              }
              return (
                <g key={k}>
                  <rect x={cx} y={y + rowH / 2 - 22} width={56} height={22} rx={5}
                    fill={c.color + "20"} stroke={c.color} strokeWidth={1} />
                  <text x={cx + 28} y={y + rowH / 2 - 6} textAnchor="middle"
                    fontFamily="'JetBrains Mono', monospace" fontSize={11} fontWeight={600}
                    fill={c.color}>{cid}</text>
                </g>
              );
            })}
          </g>
        );
      })}
    </g>
  );
}

// Cena 3 — stanza: co-ocorrência na mesma estrofe = conexão
function StanzaView({ revealed }) {
  const codeY = 360;
  const xs = { "K.det": 110, "K.part": 240, "S.med": 370, "E.evi": 500, "E.inc": 600 };
  return (
    <g>
      <StageTag n={3} label="STANZA" />
      {/* Bracket da stanza com as 5 falas resumidas */}
      <rect x={40} y={56} width={600} height={250} rx={12} fill={C.violetBg}
        stroke={C.violet} strokeWidth={1.2} strokeDasharray="6 4" />
      <text x={56} y={50} fontFamily="'JetBrains Mono', monospace" fontSize={12}
        fontWeight={700} fill={C.violet}>STANZA · Evento 4 (HYPATIA)</text>
      {LINES.map((ln, i) => (
        <g key={i}>
          <text x={64} y={88 + i * 32} fontFamily="'Atkinson Hyperlegible', sans-serif"
            fontSize={12.5} fill={C.muted}>
            <tspan fontFamily="'JetBrains Mono', monospace" fill={C.dim}>{i + 1} </tspan>
            "{ln.t.length > 52 ? ln.t.slice(0, 50) + "…" : ln.t}"
          </text>
          {ln.codes.map((cid, k) => (
            <circle key={k} cx={560 + k * 18} cy={84 + i * 32} r={5} fill={getCode(cid).color} />
          ))}
        </g>
      ))}
      {/* Os 5 elementos em linha, na base */}
      {CODES.map((c) => (
        <g key={c.id}>
          <circle cx={xs[c.id]} cy={codeY} r={9} fill={c.color} />
          <text x={xs[c.id]} y={codeY + 24} textAnchor="middle"
            fontFamily="'JetBrains Mono', monospace" fontSize={11} fontWeight={600} fill={c.color}>
            {c.id}
          </text>
        </g>
      ))}
      {/* Arcos de co-ocorrência aparecem na revelação */}
      {revealed && COOC.map(([a, b], i) => {
        const x1 = xs[a], x2 = xs[b];
        const mx = (x1 + x2) / 2;
        const lift = 40 + Math.abs(x2 - x1) * 0.12;
        return (
          <path key={i} d={`M ${x1} ${codeY - 10} Q ${mx} ${codeY - 10 - lift} ${x2} ${codeY - 10}`}
            fill="none" stroke={C.green} strokeWidth={2.4} opacity={0.7} strokeLinecap="round" />
        );
      })}
      {revealed && (
        <text x={340} y={336} textAnchor="middle" fontFamily="'Atkinson Hyperlegible', sans-serif"
          fontSize={12.5} fontStyle="italic" fill={C.green}>
          co-ocorreram na mesma stanza → conectados
        </text>
      )}
    </g>
  );
}

// Cena 4 — matriz de adjacência
function MatrixView({ revealed }) {
  const cell = 52, x0 = 215, y0 = 150;
  const pairSet = new Set();
  COOC.forEach(([a, b]) => { pairSet.add(IDX[a] + "-" + IDX[b]); pairSet.add(IDX[b] + "-" + IDX[a]); });
  const predCell = [0, 1]; // K.det × K.part — alvo da previsão
  return (
    <g>
      <StageTag n={4} label="MATRIZ DE ADJACÊNCIA" />
      <foreignObject x={x0} y={92} width={300} height={34}>
        <div xmlns="http://www.w3.org/1999/xhtml" style={{
          fontFamily: "'Atkinson Hyperlegible', sans-serif", fontSize: 14, color: C.muted,
        }}>
          matriz <K tex="C_{ij}" color={C.text} /> da stanza
        </div>
      </foreignObject>
      {/* cabeçalhos de coluna */}
      {CODES.map((c, j) => (
        <text key={"col" + j} x={x0 + j * cell + cell / 2} y={y0 - 8} textAnchor="middle"
          fontFamily="'JetBrains Mono', monospace" fontSize={11} fontWeight={600} fill={c.color}>
          {c.id}
        </text>
      ))}
      {/* cabeçalhos de linha */}
      {CODES.map((c, i) => (
        <text key={"row" + i} x={x0 - 10} y={y0 + i * cell + cell / 2 + 4} textAnchor="end"
          fontFamily="'JetBrains Mono', monospace" fontSize={11} fontWeight={600} fill={c.color}>
          {c.id}
        </text>
      ))}
      {/* células */}
      {CODES.map((_, i) =>
        CODES.map((__, j) => {
          const x = x0 + j * cell, y = y0 + i * cell;
          const diag = i === j;
          const one = pairSet.has(i + "-" + j);
          const isPred = i === predCell[0] && j === predCell[1] || i === predCell[1] && j === predCell[0];
          let fill = C.card;
          if (diag) fill = C.grayBg;
          else if (revealed && one) fill = C.greenBg;
          else if (!revealed && isPred) fill = C.amberBg;
          return (
            <g key={i + "-" + j}>
              <rect x={x} y={y} width={cell} height={cell} fill={fill}
                stroke={(!revealed && isPred) ? C.amber : C.border}
                strokeWidth={(!revealed && isPred) ? 1.6 : 0.8} />
              {diag ? (
                <text x={x + cell / 2} y={y + cell / 2 + 5} textAnchor="middle"
                  fontFamily="'JetBrains Mono', monospace" fontSize={15} fill={C.dimmer}>·</text>
              ) : revealed ? (
                <text x={x + cell / 2} y={y + cell / 2 + 5} textAnchor="middle"
                  fontFamily="'JetBrains Mono', monospace" fontSize={15} fontWeight={one ? 700 : 400}
                  fill={one ? C.green : C.dimmer}>{one ? "1" : "0"}</text>
              ) : isPred ? (
                <text x={x + cell / 2} y={y + cell / 2 + 5} textAnchor="middle"
                  fontFamily="'JetBrains Mono', monospace" fontSize={16} fontWeight={700}
                  fill={C.amber}>?</text>
              ) : null}
            </g>
          );
        })
      )}
    </g>
  );
}

// Cena 5 — a rede (ENA): conexões simétricas, E.inc fica solta
function NetworkView({ revealed }) {
  return (
    <g>
      <StageTag n={5} label="REDE EPISTÊMICA (ENA)" />
      {revealed && COOC.map(([a, b], i) => (
        <line key={i} x1={NET[a][0]} y1={NET[a][1]} x2={NET[b][0]} y2={NET[b][1]}
          stroke={C.green} strokeWidth={3.2} strokeLinecap="round" opacity={0.72} />
      ))}
      {revealed && (
        <g>
          <circle cx={NET["E.inc"][0]} cy={NET["E.inc"][1]} r={26} fill="none"
            stroke={C.amber} strokeWidth={1.5} strokeDasharray="4 4" opacity={0.8} />
          <text x={NET["E.inc"][0]} y={NET["E.inc"][1] + 44} textAnchor="middle"
            fontFamily="'Atkinson Hyperlegible', sans-serif" fontSize={12.5} fontStyle="italic"
            fill={C.amber}>solta — mencionada, não conectada</text>
        </g>
      )}
      {CODES.map((c) => <Node key={c.id} id={c.id} pos={NET[c.id]} />)}
      {revealed && (
        <text x={NET["E.evi"][0] + 18} y={NET["E.evi"][1] + 30} textAnchor="start"
          fontFamily="'Atkinson Hyperlegible', sans-serif" fontSize={12.5} fontStyle="italic"
          fill={C.green}>centro: tudo passa pela evidência</text>
      )}
    </g>
  );
}

// Cena 6 — ENA × ONA: a ordem entra (setas) na revelação
function OnaView({ revealed }) {
  return (
    <g>
      <StageTag n={6} label={revealed ? "ANÁLISE ORDENADA (ONA)" : "ENA · SIMÉTRICA"} />
      {!revealed && COOC.map(([a, b], i) => (
        <line key={i} x1={NET[a][0]} y1={NET[a][1]} x2={NET[b][0]} y2={NET[b][1]}
          stroke={C.gray} strokeWidth={3} strokeLinecap="round" opacity={0.6} />
      ))}
      {revealed && COOC_DIR.map(([a, b], i) => (
        <DirEdge key={i} from={NET[a]} to={NET[b]} color={C.violet} width={3} opacity={0.85} />
      ))}
      {CODES.map((c) => <Node key={c.id} id={c.id} pos={NET[c.id]} />)}
      {!revealed && (
        <text x={340} y={430} textAnchor="middle" fontFamily="'Atkinson Hyperlegible', sans-serif"
          fontSize={13} fontStyle="italic" fill={C.dim}>linhas sem direção: A↔B é o mesmo que B↔A</text>
      )}
      {revealed && (
        <text x={340} y={430} textAnchor="middle" fontFamily="'Atkinson Hyperlegible', sans-serif"
          fontSize={13} fontStyle="italic" fill={C.violet}>
          o raciocínio flui para a evidência — a ordem carrega informação
        </text>
      )}
    </g>
  );
}

// Cena 7 — projeção: novato (laranja) → frame-alvo (violeta)
function ProjectionView({ revealed }) {
  const cx = 340, cy = 250;
  return (
    <g>
      <StageTag n={7} label="APLICAÇÃO À TESE" />
      {/* eixos do espaço de projeção */}
      <line x1={70} y1={cy} x2={620} y2={cy} stroke={C.dimmer} strokeWidth={1} opacity={0.6} />
      <line x1={cx} y1={70} x2={cx} y2={430} stroke={C.dimmer} strokeWidth={1} opacity={0.6} />
      <text x={cx} y={64} textAnchor="middle" fontFamily="'Atkinson Hyperlegible', sans-serif"
        fontSize={11.5} fontStyle="italic" fill={C.dim}>conhecimento / dados</text>
      <text x={cx} y={446} textAnchor="middle" fontFamily="'Atkinson Hyperlegible', sans-serif"
        fontSize={11.5} fontStyle="italic" fill={C.dim}>epistemologia / colaboração</text>

      {/* rede-alvo (violeta) — só na revelação, integra a incerteza */}
      {revealed && PROJ_ALVO.map(([a, b], i) => (
        <line key={"alvo" + i} x1={PROJ[a][0]} y1={PROJ[a][1]} x2={PROJ[b][0]} y2={PROJ[b][1]}
          stroke={C.violet} strokeWidth={3} strokeLinecap="round" opacity={0.62} />
      ))}
      {/* rede do novato (laranja) — sempre visível */}
      {PROJ_NOVATO.map(([a, b], i) => (
        <line key={"nov" + i} x1={PROJ[a][0]} y1={PROJ[a][1]} x2={PROJ[b][0]} y2={PROJ[b][1]}
          stroke={C.orange} strokeWidth={3} strokeLinecap="round" opacity={0.8} />
      ))}
      {CODES.map((c) => <Node key={c.id} id={c.id} pos={PROJ[c.id]} r={9} />)}

      {/* legenda */}
      <g>
        <line x1={110} y1={410} x2={140} y2={410} stroke={C.orange} strokeWidth={3.2} strokeLinecap="round" />
        <text x={146} y={414} fontFamily="'Atkinson Hyperlegible', sans-serif" fontSize={12.5} fill={C.muted}>jogador novato</text>
        {revealed && (
          <g>
            <line x1={300} y1={410} x2={330} y2={410} stroke={C.violet} strokeWidth={3.2} strokeLinecap="round" />
            <text x={336} y={414} fontFamily="'Atkinson Hyperlegible', sans-serif" fontSize={12.5} fill={C.muted}>frame-alvo (físico do CERN)</text>
          </g>
        )}
      </g>
      {revealed && (
        <text x={PROJ["E.inc"][0] - 14} y={PROJ["E.inc"][1] + 4} textAnchor="end"
          fontFamily="'Atkinson Hyperlegible', sans-serif" fontSize={12} fontStyle="italic" fill={C.violet}>
          incerteza
        </text>
      )}
    </g>
  );
}

const VIEWS = {
  teaser: TeaserView, coding: CodingView, stanza: StanzaView,
  matrix: MatrixView, network: NetworkView, ona: OnaView, projection: ProjectionView,
};

// ════════════════════════════════════════════════════════════════════
//  ROTEIRO DECLARATIVO DAS CENAS
// ════════════════════════════════════════════════════════════════════
const SCENES = [
  {
    id: "misterio",
    title: "Cena 1 · O mistério",
    feynman:
      "Imagina dois alunos seus na Masterclass do ATLAS. Os dois sabem o que é um múon, " +
      "os dois calculam massa invariante, os dois acertam que 91 GeV é o bóson Z. Na prova, " +
      "mesma nota. Mas será que pensam igual? Vem comigo — hoje a gente vai medir uma coisa " +
      "que prova nenhuma pega.",
    prediction:
      "Se dois alunos sabem os mesmos conceitos e acertam as mesmas questões, o que ainda pode " +
      "diferenciar o modo como cada um pensa?",
    reveal:
      "O que diferencia não é ter as peças — é como elas se conectam. Um aluno liga 'medi 91 GeV' " +
      "a 'mas um evento só não basta'; o outro para no '91 GeV, achei o Z!'. Mesmo conteúdo, " +
      "estruturas de pensamento diferentes. ENA e ONA são métodos para medir exatamente essa estrutura.",
    config: { view: "teaser" },
    cta: "Como medir isso?",
  },
  {
    id: "codificar",
    title: "Cena 2 · Codificar",
    feynman:
      "Vamos lá. O ponto de partida não é nota de prova — é a fala do aluno enquanto ele explora. " +
      "Aqui está um aluno mexendo no HYPATIA, o visualizador de eventos do ATLAS, pensando alto. " +
      "A gente faz um trabalho de artesão: vai linha por linha e marca quais elementos do pensar " +
      "de um físico aparecem em cada fala. Isso se chama codificar.",
    prediction:
      "Na segunda fala — 'esse rastro chega na câmara de múons, então é um múon' — quais elementos " +
      "do pensamento de um físico estão presentes?",
    reveal:
      "Aparecem três. Ele lê o detector (K.det), identifica a partícula (K.part) e — observe que — " +
      "justifica com evidência (E.evi): o 'então é um múon' não é chute, é conclusão a partir do que " +
      "o aparelho mostra. Codificar é transformar fala em presença ou ausência desses elementos.",
    config: { view: "coding" },
    cta: "E como viram conexão?",
  },
  {
    id: "stanza",
    title: "Cena 3 · Stanza",
    feynman:
      "Agora o pulo do gato. Marcar os elementos não basta — a gente quer saber quais andam juntos. " +
      "Para isso existe a stanza. O nome vem da análise do discurso, mas pensa nas estrofes de um " +
      "poema: tudo na mesma estrofe se relaciona; o que está em estrofes diferentes, não. Na ENA, " +
      "dois elementos na mesma stanza contam como conectados.",
    prediction:
      "Se 'identificar a partícula' e 'justificar com evidência' caem na mesma stanza, o que a ENA " +
      "conclui sobre eles?",
    reveal:
      "Que estão conectados — o aluno ligou uma coisa à outra. Observe que a stanza é uma escolha sua: " +
      "pode ser uma cena, uma rodada de fala, uma janelinha de poucas linhas. E aqui o limite honesto " +
      "do método: a stanza não é dado bruto, é decisão metodológica — e você precisa justificá-la.",
    config: { view: "stanza" },
    cta: "Mostra a conta.",
  },
  {
    id: "matriz",
    title: "Cena 4 · Matriz de adjacência",
    feynman:
      "A conta é mais simples do que parece. Para cada stanza, a ENA monta uma tabelinha: para cada " +
      "par de elementos, pergunta 'apareceram juntos?'. Se sim, 1; se não, 0. É a matriz de " +
      "adjacência — o esqueleto numérico da rede.",
    prediction:
      "Na nossa stanza, 'lê o detector' e 'identifica a partícula' apareceram juntos na segunda fala. " +
      "Que número vai na célula que cruza os dois?",
    reveal:
      "Vai 1 — eles co-ocorreram. Observe que a diagonal fica vazia: um elemento não se conecta " +
      "consigo mesmo. E quando você tem muitas stanzas de um mesmo aluno, soma todas as tabelinhas: " +
      "nasce a matriz acumulada, a impressão digital do raciocínio daquele aluno.",
    config: { view: "matrix" },
    cta: "Vira desenho agora.",
  },
  {
    id: "rede",
    title: "Cena 5 · A rede (ENA)",
    feynman:
      "Vou fazer agora uma coisa que cientistas adoram fazer — abstrair. Aquela tabela de números " +
      "vira desenho: cada elemento é um ponto, cada co-ocorrência é uma linha, e quanto mais vezes " +
      "dois elementos andaram juntos, mais grossa a linha. É a rede epistêmica do aluno.",
    prediction:
      "O aluno ligou identificação, medição e evidência. E a incerteza — aquele 'um evento só não " +
      "basta' — aparece amarrada ao resto da rede, ou solta?",
    reveal:
      "Solta. Observe que a evidência virou o centro — tudo passa por ela. Mas a incerteza ficou num " +
      "cantinho, desconectada: o aluno até mencionou que um evento não basta, mas não ligou isso ao " +
      "raciocínio. Aviso de limite: a ENA trata as conexões como simétricas — se A liga a B, B liga a A. Guarda isso.",
    config: { view: "network" },
    cta: "Mas e a ordem?",
  },
  {
    id: "ona",
    title: "Cena 6 · ENA × ONA",
    feynman:
      "Aqui os pesquisadores costumam cometer um erro: achar que ENA e ONA são a mesma coisa com nome " +
      "trocado. Não são. A ENA esquece a ordem. Mas num RPG, a ordem é tudo: 'primeiro questionou a " +
      "significância, depois invocou a colaboração' conta uma história diferente do contrário.",
    prediction:
      "Numa narrativa — numa campanha de RPG que se desenrola no tempo — a ordem em que as conexões " +
      "aparecem carrega informação que a análise deveria preservar?",
    reveal:
      "Carrega, e muito. A ONA põe seta nas conexões: aqui o raciocínio flui em direção à justificativa " +
      "— ler, identificar, medir, tudo desemboca em 'justificar com evidência'. A ENA achataria isso num " +
      "emaranhado sem direção. Para medir como o frame se desenvolve ao longo da narrativa, a ONA enxerga o que a ENA não vê.",
    config: { view: "ona" },
    cta: "Recapitular e aplicar.",
  },
  {
    id: "tese",
    title: "Cena 7 · Aplicação à tese",
    feynman:
      "Vamos recapitular. Partimos da fala bruta no HYPATIA, codificamos os elementos, agrupamos em " +
      "stanzas, montamos a matriz, desenhamos a rede — e aprendemos a comparar redes. E daí, o que isso " +
      "tem a ver com a tese? Olha os dois desenhos: em laranja, um jogador novato; em violeta, o frame-alvo, " +
      "o jeito de pensar de quem trabalha no CERN.",
    prediction:
      "Em termos dessa figura, o que o EDURPG.IA precisa produzir no aluno ao longo da campanha?",
    reveal:
      "Precisa puxar o laranja em direção ao violeta — fazer as conexões migrarem do canto 'conhecimento " +
      "puro' para a região onde a incerteza, o rigor e a colaboração entram na conta. Cada princípio de design " +
      "da tese é uma alavanca nesse empurrão. A pergunta que abre a próxima etapa não é mais 'a ENA funciona?' " +
      "— é 'qual princípio de design move qual conexão, e quando a ONA captura melhor que a ENA?'.",
    config: { view: "projection" },
    cta: "Reiniciar",
  },
];

// ════════════════════════════════════════════════════════════════════
//  COMPONENTE PRINCIPAL
// ════════════════════════════════════════════════════════════════════
export default function EnaOnaSeminario() {
  const katexReady = useKatexLoader();
  const [sceneIdx, setSceneIdx] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const scene = SCENES[sceneIdx];

  useEffect(() => { setRevealed(false); }, [sceneIdx]);

  // Loop em duas fases: 1º clique revela; 2º clique avança.
  const next = () => {
    if (!revealed) { setRevealed(true); return; }
    if (sceneIdx < SCENES.length - 1) setSceneIdx(sceneIdx + 1);
    else { setSceneIdx(0); setRevealed(false); }
  };
  const prev = () => {
    if (revealed) { setRevealed(false); return; }
    if (sceneIdx > 0) setSceneIdx(sceneIdx - 1);
  };

  const View = VIEWS[scene.config.view];

  return (
    <KatexCtx.Provider value={katexReady}>
      <div className="ff-stage">
        <style>{STYLES}</style>

        <header className="ff-hero">
          <div className="ff-overline">Seminário de Tese · método</div>
          <h2>Como funciona a ENA e a ONA</h2>
          <p className="ff-subtitle">
            Da fala do aluno no HYPATIA à rede epistêmica — codificação e análise, passo a passo.
          </p>
        </header>

        <div className="ff-progress" aria-label="Progresso da apresentação">
          {SCENES.map((s, i) => (
            <button key={s.id}
              className={"ff-step" + (i === sceneIdx ? " current" : i < sceneIdx ? " done" : "")}
              onClick={() => { setSceneIdx(i); setRevealed(false); }}
              title={s.title}
              aria-current={i === sceneIdx ? "step" : undefined}>
              <span className="ff-step-num">{i + 1}</span>
            </button>
          ))}
        </div>

        <div className="ff-main">
          <section className="ff-narrative">
            <div className="ff-scene-badge">{scene.title}</div>

            <blockquote className="ff-feynman">
              <div className="ff-feynman-quote">{scene.feynman}</div>
            </blockquote>

            {!revealed && (
              <div className="ff-prediction">
                <div className="ff-prediction-icon">?</div>
                <div className="ff-prediction-body">
                  <div className="ff-prediction-label">Para discussão</div>
                  <div className="ff-prediction-text">{scene.prediction}</div>
                </div>
              </div>
            )}

            {revealed && (
              <div className="ff-reveal">
                <div className="ff-reveal-label">Análise</div>
                <div className="ff-reveal-text">{scene.reveal}</div>
              </div>
            )}

            <div className="ff-nav">
              <button className="ff-nav-btn secondary" onClick={prev}
                disabled={sceneIdx === 0 && !revealed}>
                ← Voltar
              </button>
              <button className="ff-nav-btn primary" onClick={next}>
                {!revealed
                  ? "Ver análise"
                  : (sceneIdx < SCENES.length - 1 ? scene.cta + " →" : "↻ Reiniciar")}
              </button>
            </div>
          </section>

          <section className="ff-canvas-area">
            <div className="ff-canvas-panel">
              <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`}>
                <View revealed={revealed} />
              </svg>
            </div>
            <div className="ff-legend">
              {CODES.map((c) => (
                <span key={c.id} className="ff-legend-item">
                  <span className="ff-legend-dot" style={{ background: c.color }} />
                  <span className="ff-legend-code" style={{ color: c.color }}>{c.id}</span>
                  <span className="ff-legend-desc">{c.label}</span>
                </span>
              ))}
            </div>
          </section>
        </div>
      </div>
    </KatexCtx.Provider>
  );
}

const STYLES = `
@import url('https://fonts.googleapis.com/css2?family=Atkinson+Hyperlegible:ital,wght@0,400;0,700;1,400;1,700&family=JetBrains+Mono:wght@400;600&family=Caveat:wght@500&display=swap');

.ff-stage, .ff-stage * { box-sizing: border-box; }
.ff-stage {
  font-family: 'Atkinson Hyperlegible', system-ui, sans-serif;
  color: ${C.text}; background: ${C.bg};
  padding: 18px; border-radius: 14px;
  font-size: 15px; line-height: 1.55;
  max-width: 1200px; margin: 0 auto;
}

.ff-hero { text-align: center; margin-bottom: 14px; }
.ff-overline {
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px; letter-spacing: 1.5px;
  color: ${C.violet}; font-weight: 700;
  text-transform: uppercase; margin-bottom: 6px;
}
.ff-hero h2 { font-size: clamp(20px, 1.6vw + 14px, 28px); font-weight: 700; margin: 0 0 4px; line-height: 1.2; }
.ff-subtitle { font-size: 13.5px; color: ${C.muted}; font-style: italic; margin: 0 auto; max-width: 640px; }

.ff-progress { display: flex; justify-content: center; align-items: center; gap: 8px; margin: 16px 0 18px; flex-wrap: wrap; }
.ff-step {
  width: 32px; height: 32px; border-radius: 50%;
  border: 1.5px solid ${C.border}; background: ${C.card}; color: ${C.dim};
  cursor: pointer; transition: all .2s;
  display: inline-flex; align-items: center; justify-content: center;
  font-family: 'JetBrains Mono', monospace; font-weight: 700; font-size: 13px; padding: 0;
}
.ff-step:hover { border-color: ${C.borderStrong}; color: ${C.text}; }
.ff-step.current { border-color: ${C.violet}; color: ${C.violet}; background: ${C.violetBg}; box-shadow: 0 0 0 3px ${C.violet}22; }
.ff-step.done { border-color: ${C.green}; color: ${C.green}; }

.ff-main { display: grid; grid-template-columns: 1fr; gap: 16px; }
@media (min-width: 960px) { .ff-main { grid-template-columns: 1fr 1.1fr; align-items: start; } }

.ff-narrative {
  background: ${C.card}; border: 1px solid ${C.border};
  border-radius: 14px; padding: 18px;
  box-shadow: 0 4px 18px rgba(15,23,42,0.06); min-width: 0;
}
.ff-scene-badge {
  display: inline-block; font-family: 'JetBrains Mono', monospace;
  font-size: 11px; letter-spacing: 0.8px; color: ${C.violet}; font-weight: 700;
  background: ${C.violetBg}; padding: 4px 10px; border-radius: 4px; margin-bottom: 12px;
}

.ff-feynman {
  margin: 0 0 14px; padding: 14px 16px 14px 20px;
  border-left: 3px solid ${C.amber};
  background: linear-gradient(135deg, #fff8ec, #fefcf8);
  border-radius: 0 8px 8px 0; position: relative;
}
.ff-feynman::before {
  content: '"'; position: absolute; top: -4px; left: 8px;
  font-family: 'Caveat', cursive; font-size: 42px; color: ${C.amber};
  line-height: 1; opacity: 0.7;
}
.ff-feynman-quote { font-size: 15.5px; line-height: 1.6; color: ${C.text}; font-style: italic; padding-left: 14px; }

.ff-prediction {
  display: flex; gap: 12px; background: ${C.violetBg};
  border: 1.5px solid ${C.violet}66; border-radius: 10px;
  padding: 12px 14px; margin-bottom: 14px;
}
.ff-prediction-icon {
  flex-shrink: 0; width: 28px; height: 28px; border-radius: 50%;
  background: ${C.violet}; color: white;
  display: inline-flex; align-items: center; justify-content: center;
  font-weight: 700; font-size: 16px; font-family: 'JetBrains Mono', monospace;
}
.ff-prediction-body { flex: 1; min-width: 0; }
.ff-prediction-label {
  font-family: 'JetBrains Mono', monospace; font-size: 10.5px; letter-spacing: 0.6px;
  color: ${C.violet}; font-weight: 700; text-transform: uppercase; margin-bottom: 4px;
}
.ff-prediction-text { font-size: 14.5px; line-height: 1.5; color: ${C.text}; }

.ff-reveal {
  background: ${C.greenBg}; border: 1.5px solid ${C.green}66; border-radius: 10px;
  padding: 12px 14px; margin-bottom: 14px; animation: ff-fadein .3s ease;
}
@keyframes ff-fadein { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
.ff-reveal-label {
  font-family: 'JetBrains Mono', monospace; font-size: 10.5px; letter-spacing: 0.6px;
  color: ${C.green}; font-weight: 700; text-transform: uppercase; margin-bottom: 6px;
}
.ff-reveal-text { font-size: 14.5px; line-height: 1.55; color: ${C.text}; }

.ff-nav { display: flex; gap: 10px; justify-content: space-between; margin-top: 12px; }
.ff-nav-btn {
  padding: 9px 16px; border-radius: 8px; font-family: 'Atkinson Hyperlegible', sans-serif;
  font-size: 14px; font-weight: 700; cursor: pointer; transition: all .15s;
  border: 1.5px solid; min-height: 38px;
}
.ff-nav-btn.primary { background: ${C.violet}; color: white; border-color: ${C.violet}; }
.ff-nav-btn.primary:hover { background: ${C.violetSoft}; border-color: ${C.violetSoft}; }
.ff-nav-btn.secondary { background: transparent; color: ${C.dim}; border-color: ${C.border}; }
.ff-nav-btn.secondary:hover:not(:disabled) { border-color: ${C.borderStrong}; color: ${C.text}; }
.ff-nav-btn:disabled { opacity: 0.4; cursor: not-allowed; }

.ff-canvas-area { min-width: 0; }
.ff-canvas-panel {
  background: ${C.card}; border: 1px solid ${C.border}; border-radius: 14px;
  padding: 6px; box-shadow: 0 4px 18px rgba(15,23,42,0.06); position: relative; overflow: hidden;
}
.ff-canvas-panel svg { display: block; width: 100%; height: auto; aspect-ratio: ${WIDTH} / ${HEIGHT}; user-select: none; }

.ff-legend {
  display: flex; flex-wrap: wrap; gap: 6px 14px; margin-top: 10px; padding: 4px 6px;
}
.ff-legend-item { display: inline-flex; align-items: center; gap: 5px; font-size: 12px; }
.ff-legend-dot { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }
.ff-legend-code { font-family: 'JetBrains Mono', monospace; font-weight: 600; font-size: 11.5px; }
.ff-legend-desc { color: ${C.muted}; }

.fi-katex { display: inline-block; }
.fi-katex .katex { font-size: 1em; }
`;
